import { particles } from 'app/contracts/Particles';
import { geometry } from 'app/contracts/Geometry';

import { ParticleLayoutType } from 'app/enum/Particles';
import { ParticleManager } from 'particles/ParticleManager';
import { Particle } from 'particles/Particle';
import DelayHelper from 'utils/timers';

export class ParticleField implements particles.ParticleField {
	//NOTE: read in tick() but never assigned anywhere in the codebase — the guard
	//is always false, so the shock effect is currently dead:
	public shock: { time: number };

	private _particleManager: ParticleManager;
	protected _sceneProps: particles.ParticlesSceneProps;
	private _id: string;
	private _fieldConfig: particles.ParticleFieldConfig;
	protected _particleContainer: PIXI.Container;
	private _subContainers = null;

	constructor(sceneProps: particles.ParticlesSceneProps, container: PIXI.Container, config: particles.ParticleFieldConfig) {
		this._particleContainer = new PIXI.Container();

		container.addChild(this._particleContainer);

		//Optional injected scene graph: a caller can hand over a pre-built keyed
		//set of sub-containers, or a factory invoked here — at exactly the point
		//the home scene-graph block used to run inline — for sub-trees that need
		//the just-created particle container (flags, masks, child order). The
		//home view uses the factory: see app/views/home/webgl/particles/scenes/ParticlesStage.ts.
		if (config.subContainersFactory) {
			this._subContainers = config.subContainersFactory(this._particleContainer);
		} else if (config.subContainers) {
			this._subContainers = config.subContainers;
		}

		this._id = config.id;
		//Kept by reference: subclasses may assign config.particleFactory right
		//after super() — particle creation is deferred (setTimeout), so the
		//factory is always in place before the first particle is constructed.
		this._fieldConfig = config;
		this._sceneProps = sceneProps;

		//Offload everything to a normal particle manager:
		let managerConfig: particles.ParticleSetConfig = this._createConfig(config);
		this._particleManager = new ParticleManager(this._sceneProps, container);

		let delayInitTime = this._addParticles(managerConfig);

		setTimeout(() => {
			this.init();
		}, delayInitTime + 50);

	}

	//NOTE: both bodies below were already dead (unreachable after `return;`)
	//before the home scene-graph extraction; kept as the no-ops they always were.
	public hideAllButTransition() {
		return;
	}

	public showAllButTransition() {
		return;
	}

	//GETTER / SETTER
	//-------------------------------------------------------------------------------------
	public get particles(): Array<particles.Particle> {
		return this._particleManager.particles;
	}

	public get id(): string {
		return this._id;
	}

	//PUBLIC METHODS
	//-------------------------------------------------------------------------------------

	public getSubContainer(type: string) {
		return this._subContainers[type];
	}

	public tick(): void {
		this._particleContainer.sortableChildren = false;
		this._particleManager.tick();
		if (this.shock) {
			this.shock.time = this.shock.time >= 1 ? 0 : this.shock.time + 0.01;
		}
	}

	public resize(): void {
		this._particleManager.resize();
	}

	public destroy(): void {
		this._particleManager.destroy();
	}

	public removeAllTags(): void {
		for (let i = 0; i < this.particles.length; i++) {
			this.particles[i].removeAllTags();
		}
	}

	public addTagToParticles(tag: string, particles: Array<particles.Particle> = null): void {
		let targetParticles: Array<particles.Particle> = particles || this.particles;
		targetParticles.forEach((particle: particles.Particle, index: number) => {
			particle.addTag(tag);
		});
	}

	public removeTagFromParticles(tag: string, particles: Array<particles.Particle> = null): void {
		let targetParticles: Array<particles.Particle> = particles || this.particles;
		targetParticles.forEach((particle: particles.Particle, index: number) => {
			particle.removeTag(tag);
		});
	}

	public particlesByTag(tag: string, particles: Array<particles.Particle> = null): Array<particles.Particle> {
		let targetParticles: Array<particles.Particle> = particles || this.particles;
		return targetParticles.filter((particle: particles.Particle) => {
			return particle.hasTag(tag);
		});
	}

	public particlesFromIndex(
		startIndex: number = 0,
		amount: number = null,
		particles: Array<particles.Particle> = null
	): Array<particles.Particle> {
		let targetParticles: Array<particles.Particle> = particles || this.particles;
		amount = amount || targetParticles.length;
		return targetParticles.slice(startIndex, startIndex + amount);
	}

	//Set the target position of the particles
	//Note this most likely be overidden by other
	public reposition(
		type: ParticleLayoutType,
		position: geometry.Vector2,
		size: geometry.Size,
		config: any = null,
		particles: Array<particles.Particle> = null,
		randomSeed: number = 0,
		instant: boolean = false,
		onStart: CallableFunction = null,
		skipItems = false
	): void {
		//Get the layout instance for the correct type:
		let layout: particles.ParticleLayout = this._sceneProps.layouts[type];

		//Target particles passed or all
		let targetParticles: Array<particles.Particle> = particles || this.particles;

		//Get an array of positions to set the partciles to:
		let positions: Array<particles.ParticleLookupProps> = layout.get(
			targetParticles.length,
			position,
			size,
			config,
			skipItems
		);

		//Loop through and set the position:
		targetParticles.forEach((particle, index: number) => {
			//particle.position is a shortcut to whatever behaviour is setting the particles base position


			if (positions[index]) {
				particle.position.xOffset = 0;
				particle.position.set({ x: positions[index].x, y: positions[index].y }, instant);

				if (positions[index].scale) {
					particle.animatedProps.targetScale = positions[index].scale || 0;
				}

				if (positions[index].alpha) {
					particle.animatedProps.targetAlpha = positions[index].alpha || 0.5;
				}

				particle.animatedProps.targetRotation = positions[index].rotation || 0;
			} else {
				particle.position.set({
					x: 0.81 + Math.random() * 0.12,
					y: 0.41 + Math.random() * 0.25
				});
				particle.animatedProps.hardSetScale(0.00000001);
				particle.animatedProps.alpha = particle.animatedProps.targetAlpha = 0.00000001;
				particle.animatedProps.targetRotation = 0;
			}

			if (onStart) {
				onStart(particle);
			}

		});
	}

	protected repositionFromCache(
		cacheData: Array<any>,
		particles: Array<particles.Particle> = null,
		randomSeed: number = 0,
		instant: boolean = false,
		skipScaleAlpha: boolean = false
	): void {
		//Loop through and set the position:
		particles.forEach((particle, index: number) => {
			//particle.position is a shortcut to whatever behaviour is setting the particles base position
			DelayHelper.setTimeout(() => {
				this.positionOneFromCache(cacheData[index], particle, instant, skipScaleAlpha);
			}, Math.random() * randomSeed);
		});
	}

	public positionOneFromCache(
		cacheData,
		particle,
		instant,
		lerp: boolean = false,
		lerpFrames: number = 90,
		lerpCallback: CallableFunction = null,
		skipScaleAlpha = false
	) {
		particle.position.xOffset = cacheData.xOffset || 0;

		if (lerp) {
			particle.position.lerp({ x: cacheData.x, y: cacheData.y }, lerpFrames, lerpCallback);
		} else {
			particle.position.set({ x: cacheData.x, y: cacheData.y }, instant);
		}

		if (!skipScaleAlpha) {
			particle.animatedProps.targetScale = cacheData.scale || 0;
			particle.animatedProps.targetAlpha = cacheData.alpha || 1;
			particle.animatedProps.targetRotation = cacheData.rotation || 0;
		}
	}

	protected init(): void {}

	//PRIVATE METHODS
	//------------------------------------------------------------------------
	private _createConfig(fieldConfig: particles.ParticleFieldConfig): particles.ParticleSetConfig {
		let particlesConfig: Array<particles.ParticleConfig> = [];
		let textures: Array<PIXI.Texture> = fieldConfig.particles.textures;
		let tints: Array<number> = fieldConfig.particles.tints;

		let tintIndex: number = 0;

		let layoutPoints: Array<particles.ParticleLookupProps> = this._sceneProps.layouts.grid.get(
			fieldConfig.count,
			fieldConfig.position,
			fieldConfig.size
		);

		//Create all the p
		let layout: particles.ParticleLookupProps;
		let spareLayout: particles.ParticleLookupProps = {
			x: 0,
			y: 0,
			scale: 0.000001,
			alpha: 0.000001
		};

		for (let i = 0; i < fieldConfig.count; i++) {
			layout = layoutPoints[i] || spareLayout;

			let particleConfig: particles.ParticleConfig = {
				base: {
					id: fieldConfig.id + '-' + i,
					type: fieldConfig.particles.types,
					texture: textures[0],
					tint: tints[tintIndex],
					z: fieldConfig.z
				},
				intialState: {
					x: layout.x,
					y: layout.y,
					alpha: layout.alpha || 0.4,
					scale: layout.scale || 0.2,
					rotation: layout.rotation || 0
				}
			};

			tintIndex++;
			if (tintIndex === tints.length) {
				tintIndex = 0;
			}

			particlesConfig.push(particleConfig);
		}

		//Create the actual config object:
		let particleConfig: particles.ParticleSetConfig = {
			particles: particlesConfig,
			fields: null
		};

		return particleConfig;
	}

	private _processParticles(config: particles.ParticleSetConfig, delay, lowerIndex, upperIndex) {

		setTimeout(() => {
			for (let i = lowerIndex; i <= upperIndex; i++) {
				if (!config.particles[i]) {
					break;
				}

				this._processParticle(config.particles[i], i);
			}
		}, delay);
	}

	private _processParticle(particleConfig: particles.ParticleConfig, index: number) {
		//Fields that need subclass particles and/or per-index target containers
		//inject a factory (home + grid fields do); otherwise a base Particle is
		//created in the field's default container:
		if (this._fieldConfig.particleFactory) {
			this._particleManager.addParticleInstance(this._fieldConfig.particleFactory(particleConfig, index));
		} else {
			this._particleManager.addParticle(Particle, particleConfig, null);
		}
	}

	private _addParticles(config: particles.ParticleSetConfig): number {
		this._particleManager.setContainer(this._particleContainer);

		let processTimeMs = 16;
		let processPerFrame = 200;
		let index = 0;
		let delay = 0;

		while (index < config.particles.length) {
			let lowerIndex = index;
			let upperIndex = index + processPerFrame;
			this._processParticles(config, delay, lowerIndex, upperIndex);

			index = upperIndex + 1;

			delay += processTimeMs;
		}

		return delay;

	}
}
